import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles, BrainCircuit, TrendingUp, ShieldCheck, Wrench
} from 'lucide-react';
import AIRiskPanel from '../components/v2/AIRiskPanel';
import { useMQTT } from '../context/MQTTContext';
import { computeRisk } from '../lib/ai';

function trend(history, key) {
  if (history.length < 4) return 0;
  const recent = history.slice(-8);
  const first = recent[0][key] ?? 0;
  const last  = recent[recent.length - 1][key] ?? 0;
  return last - first;
}

function predictMaintenance({ vibration, battery }) {
  const items = [];
  if (vibration > 11) {
    items.push({
      title: 'Inspect mounting hardware',
      eta: '7–14 days',
      reason: 'Vibration trending above baseline → fastener fatigue likely.'
    });
  }
  if (battery < 35) {
    items.push({
      title: 'Recharge / swap battery pack',
      eta: 'within 24 h',
      reason: 'State of charge below planning threshold.'
    });
  }
  if (items.length === 0) {
    items.push({
      title: 'No maintenance required',
      eta: '—',
      reason: 'All systems within normal operating range.'
    });
  }
  return items;
}

export default function AIInsightsPage() {
  const { sensorData, history } = useMQTT();
  const { score, level } = computeRisk(sensorData);

  const trends = useMemo(() => ({
    temperature: trend(history, 'temperature'),
    humidity:    trend(history, 'humidity'),
    vibration:   trend(history, 'vibration'),
    battery:     trend(history, 'battery')
  }), [history]);

  const maintenance = predictMaintenance(sensorData);

  return (
    <div className="grid gap-4 sm:gap-5">
      <motion.div layout className="card-surface p-5 flex items-center gap-3">
        <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 grid place-items-center text-white shadow-glow">
          <BrainCircuit className="w-5 h-5" />
        </span>
        <div className="flex-1">
          <h2 className="text-lg font-extrabold tracking-tight">AI Insights</h2>
          <p className="text-sm opacity-70">
            Live analysis · score <span className="font-bold tabular-nums">{score}</span> · level <span className="font-bold uppercase">{level}</span>
          </p>
        </div>
      </motion.div>

      <AIRiskPanel />

      <div className="grid sm:grid-cols-2 gap-4">
        <motion.div layout className="card-surface p-5">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-indigo-500" />
            <p className="text-sm font-bold uppercase tracking-widest">Trend Analysis</p>
          </div>
          <div className="grid gap-2.5">
            {Object.entries(trends).map(([k, v]) => {
              const up = v > 0.05;
              const down = v < -0.05;
              const arrow = up ? '↑' : down ? '↓' : '→';
              const color = up ? '#ef4444' : down ? '#10b981' : '#94a3b8';
              return (
                <div key={k} className="flex items-center justify-between p-2.5 rounded-xl bg-black/[0.03] dark:bg-white/[0.04]">
                  <span className="capitalize text-sm font-semibold">{k}</span>
                  <span className="tabular-nums font-bold" style={{ color }}>
                    {arrow} {Math.abs(v).toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        </motion.div>

        <motion.div layout className="card-surface p-5">
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="w-4 h-4 text-amber-500" />
            <p className="text-sm font-bold uppercase tracking-widest">Predictive Maintenance</p>
          </div>
          <div className="grid gap-2.5">
            {maintenance.map((m, i) => (
              <div key={i} className="p-3 rounded-xl bg-black/[0.03] dark:bg-white/[0.04]">
                <div className="flex items-center justify-between">
                  <p className="font-bold text-sm">{m.title}</p>
                  <span className="chip bg-amber-500/15 text-amber-600 dark:text-amber-400">{m.eta}</span>
                </div>
                <p className="text-xs opacity-70 mt-1">{m.reason}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <motion.div layout className="card-surface p-5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-fuchsia-500" />
          <p className="text-sm font-bold uppercase tracking-widest">Environment Analysis</p>
        </div>
        <p className="text-sm opacity-80 leading-relaxed">
          {sensorData.temperature > 35
            ? `Conditions are warm at ${sensorData.temperature.toFixed(1)}°C. `
            : sensorData.temperature < 5
              ? `Conditions are cold at ${sensorData.temperature.toFixed(1)}°C. `
              : `Temperature is comfortable at ${sensorData.temperature.toFixed(1)}°C. `}
          {sensorData.humidity > 80
            ? 'Humidity is high — keep an eye on condensation. '
            : sensorData.humidity < 30
              ? 'Air is quite dry. '
              : 'Humidity is in a healthy range. '}
          {sensorData.vibration > 12
            ? 'Recent vibration suggests rough handling or transit.'
            : 'Container appears stable.'}
        </p>
        <div className="mt-3 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-500" />
          <span className="text-xs opacity-70 font-medium">Generated locally from live telemetry — no data leaves your device.</span>
        </div>
      </motion.div>
    </div>
  );
}
